import invariant from "invariant";
import { APIEndpoint, SingleURLScheme, UnlistableURLScheme } from "api";

import Campaign  from "models/campaign";
import CampaignAvailability from "models/campaignAvailability";
import Checkpoint from "models/checkpoint";
import Contact from "models/contact";
import Identity from "models/identity";
import ContactAttribute from "models/contactAttribute";
import Image from "models/image";
import UnsplashImage from "models/unsplashImage";
import SampleTarget from "models/sampleTarget";
import Event from "models/event";
import Action from "models/action";
import Organization from "models/organization";
import Role from "models/role";
import Conversion from "models/conversion";
import Segment from "models/segment";
import Self from "models/self";
import User from "models/user";
import Template from "models/template";
import Rate from "models/rate";
import RateGroup from "models/rateGroup";
import Performance from "models/performance";
import Module, { ModuleDetails } from "models/module";
import Greencopper from "models/greencopper";
import ImportRequest from "models/importRequest";
import Voucherset from "models/voucherset";
import GreencopperReport from "models/greencopperReport";
import MailReport from "models/mailReport";
import Domain from "models/domain";
import SegmentLog from "models/segmentLog";
import Plan from "models/plan";
import Redirection from "models/redirection";
import ShortenerDomain from "models/shortenerDomain";
import GenericTemplate from "models/genericTemplate";
import GlobalGenericTemplate from "models/globalGenericTemplate";
import ExportRequest from "exportController/model";
import Credit from "models/credit";
import Structure from "models/structure";
import Folder from "models/folder";
import { Campaign as FeedbackCampaign, Feedback } from "models/feedback";
import Webhook from "models/webhook";
import WhitelistEmail from "models/whitelistEmail";
import Formatter from "models/formatter";
import Tag from "models/tag";
import ContactCount from "models/contactCount";
import OrganizationInfoTicket from "models/organizationInfoTicket";


function ensure(value) {
	invariant(value || value === 0, `value is emtpy: ${value}`);
	return value;
}

export const unsplashGallery = new APIEndpoint(
	({ search }) => `api://unsplash/${ search ? 'search/photos' : 'photos'}`,
	UnsplashImage,
	{authorization: `Client-ID ${window.weezConfig.unsplash}`, fetchOptions: {credentials: 'omit'}}
);

export const roles = new APIEndpoint("/target/roles/", Role);
export const redirections = new APIEndpoint(
	({ organizerId, domain }) => `/target/organizations/${ensure(organizerId)}${ domain ? `/shortener-domains/${domain}` : '' }/redirections`,
	Redirection,
);
export const shortenerDomains = new APIEndpoint(({ organizerId }) => `/target/organizations/${ensure(organizerId)}/shortener-domains`, ShortenerDomain);
export const conversions = new APIEndpoint("/target/conversions", Conversion);
export const invoicePlans = new APIEndpoint("/target/invoice-plan", Plan);
export const currentPlan = new APIEndpoint(({ organizerId }) => `/target/organizations/${organizerId}/invoice`, Plan);
export const invoiceContacts = new APIEndpoint(({ organizerId }) => `/target/organizations/${organizerId}/invoice-contact/contacts`);
export const imageGallery = new APIEndpoint(({ organizerId }) => `/target/organizations/${organizerId}/gallery`, Image);
export const structureGallery = new APIEndpoint(({ organizerId }) => `/target/organizations/${organizerId}/structure/gallery`, Image);
export const campaignConversions = new APIEndpoint(({ organizerId }) => `/target/organizations/${organizerId}/conversions`, Conversion);
export const campaignConversionsList = new APIEndpoint(({ organizerId }) => `/target/organizations/${organizerId}/conversions`, Conversion);
export const users = new APIEndpoint("/target/users", User);
export const usersOrganization = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${ensure(organizerId)}/users`,
	User,
	{name: 'UserOrganization'}
);
export const contacts = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/contacts`,
	Contact
);

export const contactsTotalCount = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/contacts/get_count`,
	ContactCount,
);

export const capacities = new APIEndpoint("/target/capacities/");
export const organizations = new APIEndpoint("/target/organizations", Organization);
export const campaigns = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/campaigns`,
	Campaign,
);
export const quickCampaign = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/quick-campaign`,
);
export const campaignFolders = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/campaigns/folders`,
	Folder
);
export const segmentFolders = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/segments/folders`,
	Folder
);
export const exportCampaign = new APIEndpoint(
	({ organizerId, campaignId }) => `/target/organizations/${ensure(organizerId)}/campaigns/${ensure(campaignId)}/export/`,
	ExportRequest,
);
export const exportSegment = new APIEndpoint(
	({ organizerId, segmentId }) => `/target/organizations/${ensure(organizerId)}/segments/${ensure(segmentId)}/export/`,
	ExportRequest,
);
export const exportStructure = new APIEndpoint(
	({ organizerId, structureId }) => `/target/organizations/${ensure(organizerId)}/structures/${ensure(structureId)}/export/`,
	ExportRequest,
);
export const exportCampaignContacts = new APIEndpoint(
	({ organizerId, campaignId }) => `/target/organizations/${ensure(organizerId)}/campaigns/${ensure(campaignId)}/export/contacts/`,
	ExportRequest,
);
export const exportContacts = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${ensure(organizerId)}/export/contacts/`,
	ExportRequest,
);
export const exportCampaignsList = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${ensure(organizerId)}/export/campaigns/`,
	ExportRequest,
);
export const exportStructures = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${ensure(organizerId)}/export/structures/`,
	ExportRequest,
);
export const exportSegments = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${ensure(organizerId)}/export/segments/`,
	ExportRequest,
);
export const availability = new APIEndpoint(
	new SingleURLScheme(({ organizerId, campaignId }) => `/target/organizations/${organizerId}/campaigns/${campaignId}/plan_availability`),
	CampaignAvailability
);
export const campaignMailReport = new APIEndpoint(
	new SingleURLScheme(({ organizerId, campaignId }) => `/target/organizations/${organizerId}/campaigns/${campaignId}/report`),
	MailReport,
);
export const ageHistogramData = new APIEndpoint(
	new SingleURLScheme(({ organizerId }) => `/target/organizations/${organizerId}/contacts/age_histogram`),
);

export const langHistogramData = new APIEndpoint(
	new SingleURLScheme(({ organizerId }) => `/target/organizations/${organizerId}/contacts/lang_histogram`),
);
export const genderHistogramData = new APIEndpoint(
	new SingleURLScheme(({ organizerId }) => `/target/organizations/${organizerId}/contacts/gender_contact`),
);
export const customAttributesData = new APIEndpoint(
	new UnlistableURLScheme(({ organizerId }) => `/target/organizations/${organizerId}/contact-attributes`),
);
export const comChannelHistogramData = new APIEndpoint(
	new SingleURLScheme(({ organizerId }) => `/target/organizations/${organizerId}/contacts/communication_channel_contacts`),
);

export const campaignGreencopperReport = new APIEndpoint(
	new SingleURLScheme(({ organizerId, campaignId }) => `/target/organizations/${organizerId}/campaigns/${campaignId}/report`),
	GreencopperReport,
);
export const segments = new APIEndpoint(({ organizerId }) => `/target/organizations/${ensure(organizerId)}/segments`, Segment);

export const segmentLogs = new APIEndpoint(({ organizerId, segmentId }) => `/target/organizations/${ensure(organizerId)}/segments/${segmentId}/logs`,
	SegmentLog);
export const segmentContacts = new APIEndpoint(
	({ organizerId, segmentId }) => `/target/organizations/${organizerId}/segments/${segmentId}/contacts`,
	Contact,
	{ name: 'SegmentContact' }
);
export const segmentContactsTotalCount = new APIEndpoint(
	({ organizerId, segmentId }) => `/target/organizations/${organizerId}/segments/${segmentId}/contacts/get_count`,
	ContactCount,
);

export const includeSegmentContact = new APIEndpoint(
	({ organizerId, segmentId }) => `/target/organizations/${organizerId}/segments/${segmentId}/include`
);

export const excludeSegmentContact = new APIEndpoint(
	({ organizerId, segmentId }) => `/target/organizations/${organizerId}/segments/${segmentId}/exclude`,
);

export const events = new APIEndpoint(({ organizerId }) => `/target/organizations/${organizerId}/events`, Event);

export const performances = new APIEndpoint(
	({ organizerId, eventId }) => `/target/organizations/${ensure(organizerId)}/events/${ensure(eventId)}/performances`,
	Performance);

export const rates = new APIEndpoint(({ organizerId, eventId }) => `/target/organizations/${organizerId}/events/${eventId}/rates`, Rate);

export const rateGroups = new APIEndpoint(({ organizerId, eventId }) => `/target/organizations/${organizerId}/events/${eventId}/rate_groups`, RateGroup);

export const checkpoints = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/checkpoints`,
	Checkpoint
);

export const contactCampaigns = new APIEndpoint(
	({ organizerId, contactId }) => `/target/organizations/${organizerId}/contacts/${contactId}/campaigns`,
	Campaign,
);

export const actions = new APIEndpoint(({ organizerId }) => `/target/organizations/${organizerId}/actions`);
export const contactActions = new APIEndpoint(
	({ organizerId, contactId }) => `/target/organizations/${organizerId}/contacts/${contactId}/actions`,
	Action,
);
export const contactSegments = new APIEndpoint(
	({ organizerId, contactId }) => `/target/organizations/${organizerId}/contacts/${contactId}/segments`,
	Segment,
);
export const contactStructures = new APIEndpoint(
	({ organizerId, contactId }) => `/target/organizations/${organizerId}/contacts/${contactId}/structures`,
	Structure,
);
export const channels = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/channels`,
	{ name: 'Channel' },
);
// Templates available for the campaigns of the organization
export const templates = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${ensure(organizerId)}/templates`,
	Template,
);
// Generic templates edited by the organization
export const genericTemplates = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${ensure(organizerId)}/generic-templates`,
	GenericTemplate,
);
// Templates for the Organization 0
// cf https://github.com/Weezevent/drax/pull/994#pullrequestreview-389283365
export const weezTemplates = new APIEndpoint(
	() => '/mail/organizations/0/templates/crm-mail',
	Template,
	{ fetchOptions: { credentials: 'omit' }}
);
// Generic Templates collections currated by Weezevent
export const globalGenericTemplates = new APIEndpoint(
	() => '/mail/generic-templates/crm-mail',
	GlobalGenericTemplate,
	{ fetchOptions: { credentials: 'omit' }}
);
export const identities = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/identities`,
	Identity,
);
export const attributes = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/contact-attributes`,
	ContactAttribute,
);
export const structureAttributes = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/structure-attributes`,
	ContactAttribute,
);
export const domains = new APIEndpoint('/target/domains', Domain );

export const feedbackCampaign = new APIEndpoint('/target/feedback-campaigns', FeedbackCampaign);

export const feedbacks = new APIEndpoint(
	({ campaignSlug }) => `/target/feedback-campaigns/${campaignSlug}/feedbacks`,
	Feedback);

export const domainsOrganization = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${ensure(organizerId)}/domains`,
	Domain,
	{ name: "DomainOrganization" }
);
export const sampleTargets = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/sample-targets`,
	SampleTarget,
);
export const cguValidation = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/cgu`,
);

export const modules = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/modules`,
	Module,
);

export const moduleDetails = new APIEndpoint(
	new SingleURLScheme(({ organizerId, moduleId }) => `/target/organizations/${organizerId}/modules/${moduleId}/report`),
	ModuleDetails
);

export const greencopper = new APIEndpoint(
	new SingleURLScheme(({ organizerId }) => `/target/organizations/${organizerId}/greencopper`),
	Greencopper
);

export const importRequests = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/import/`,
	ImportRequest,
);

export const vouchersets = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/vouchers`,
	Voucherset
);

export const structures = new APIEndpoint(
	({ organizerId }) => `/target/organizations/${organizerId}/structures`,
	Structure
);

export const mergeStructures = new APIEndpoint(
	({ organizerId, structureId }) => `/target/organizations/${organizerId}/structures/${structureId}/merge`,
);

export const contactFunction = new APIEndpoint(
	new SingleURLScheme(({ organizerId, contactId, segmentId }) => `/target/organizations/${organizerId}/segments/${segmentId}/contacts/${contactId}`)
);

export const languages = new APIEndpoint(
	new SingleURLScheme(({ organizerId }) => `/target/organizations/${organizerId}/contacts/langs_in_organization`),
);

export const smsCredit = new APIEndpoint(
	new SingleURLScheme(({ organizerId }) => `/target/organizations/${organizerId}/credits/sms`),
	Credit
);

export const self = new APIEndpoint(
	new SingleURLScheme(() => `/target/_self/`),
	Self
);

export const webhooks = new APIEndpoint(
	({ organizerId }) => `/webhook/organizations/${organizerId}/webhook`,
	Webhook,
	{ fetchOptions: { credentials: 'omit' }}
);

export const whitelistAddresses = new APIEndpoint(
	({ organizerId }) => `/mail/organizations/${organizerId}/addresses`,
	WhitelistEmail,
	{ fetchOptions: { credentials: 'omit' }}
);

export const formatter = new APIEndpoint(
	({ organizerId }) => `/webhook/organizations/${organizerId}/formatter`,
	Formatter,
	{ fetchOptions: { credentials: 'omit' }}
);

export const tags = new APIEndpoint(
	({ organizerId }) => `/ticket/organizations/${organizerId}/tags`,
	Tag,
	{ fetchOptions: { credentials: 'omit' }}
);

export const organizationInfoTicket = new APIEndpoint(
	() => `/ticket/organizations`,
	OrganizationInfoTicket,
	{ fetchOptions: { credentials: 'omit' }}
);
